import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BidFormModal from './components/BidFormModal';
import BidDetailModal from './components/BidDetailModal';
import BidStatusBadge from './components/BidStatusBadge';
import BidMobileCard from './components/BidMobileCard';
import BidTypeSelectionModal from './components/BidTypeSelectionModal';
import { bidService } from '../../services/bidService';
import { supabase } from '../../lib/supabase';
import { createRealtimeSubscription, RealtimeErrorType } from '../../utils/realtimeErrorHandler';

const ALL_COLUMNS = [
  { key: 'bidNumber', label: 'Bid Number', sortable: true, required: true },
  { key: 'customerName', label: 'Customer', sortable: true },
  { key: 'projectName', label: 'Project Name', sortable: true },
  { key: 'totalAmount', label: 'Total Amount', sortable: true },
  { key: 'createdDate', label: 'Created Date', sortable: true },
  { key: 'projectStartDate', label: 'Project Start Date', sortable: true },
  { key: 'projectEndDate', label: 'Project End Date', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false, required: true },
];

const DEFAULT_VISIBLE = ['bidNumber', 'customerName', 'projectName', 'totalAmount', 'createdDate', 'status', 'actions'];
const DEFAULT_FILTERS = { search: '', status: 'all' };
const DEFAULT_SORT = { column: 'createdDate', direction: 'desc' };

const BidManagement = () => {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeError, setRealtimeError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Table preferences
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const columnPickerRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [selectedBid, setSelectedBid] = useState(null);
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false);

  useEffect(() => {
    loadBids();
    loadPreferences();

    const handleError = (error) => {
      setRealtimeError(error);
      setIsRetrying(error?.willRetry || false);
      if (!error?.willRetry && error?.type !== RealtimeErrorType?.CONNECTION_FAILED) {
        setTimeout(() => setRealtimeError(null), 5000);
      }
    };

    const { cleanup } = createRealtimeSubscription({
      supabase,
      channelName: 'bids_changes',
      tableName: 'bids',
      onDataChange: () => {
        loadBids();
        setRealtimeError(null);
        setIsRetrying(false);
      },
      onError: handleError
    });

    return cleanup;
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnPickerRef?.current && !columnPickerRef?.current?.contains(e?.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      const { data, error: prefError } = await supabase
        ?.from('user_bid_table_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();
      if (data && !prefError) {
        setVisibleColumns(data?.visible_columns || DEFAULT_VISIBLE);
        setFilters(data?.saved_filters || DEFAULT_FILTERS);
        setSort({ column: data?.sort_column || 'createdDate', direction: data?.sort_direction || 'desc' });
      }
    } catch (err) {
      // Use defaults
    } finally {
      setPrefsLoaded(true);
    }
  };

  const savePreferences = async (newVisible, newFilters, newSort) => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;
      await supabase
        ?.from('user_bid_table_preferences')
        ?.upsert({
          user_id: user?.id,
          visible_columns: newVisible,
          saved_filters: newFilters,
          sort_column: newSort?.column,
          sort_direction: newSort?.direction,
        }, { onConflict: 'user_id' });
    } catch (err) {
      console.error('Failed to save bid preferences:', err?.message);
    }
  };

  const loadBids = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await bidService?.getAll();
      if (fetchError) throw (fetchError instanceof Error ? fetchError : new Error(fetchError?.message || 'Failed to load bids'));
      setBids(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load bids');
      setBids([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (colKey) => {
    const col = ALL_COLUMNS?.find(c => c?.key === colKey);
    if (!col?.sortable) return;
    const newSort = sort?.column === colKey
      ? { column: colKey, direction: sort?.direction === 'asc' ? 'desc' : 'asc' }
      : { column: colKey, direction: 'asc' };
    setSort(newSort);
    savePreferences(visibleColumns, filters, newSort);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    savePreferences(visibleColumns, newFilters, sort);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
    savePreferences(visibleColumns, DEFAULT_FILTERS, sort);
  };

  const handleToggleColumn = (colKey) => {
    const col = ALL_COLUMNS?.find(c => c?.key === colKey);
    if (col?.required) return;
    const newVisible = visibleColumns?.includes(colKey)
      ? visibleColumns?.filter(k => k !== colKey)
      : [...visibleColumns, colKey];
    setVisibleColumns(newVisible);
    savePreferences(newVisible, filters, sort);
  };

  const handleCreateBid = async (bidData) => {
    try {
      const { data, error: createError } = await bidService?.create(bidData);
      if (createError) throw (createError instanceof Error ? createError : new Error(createError?.message || 'Failed to create bid'));
      await loadBids();
      setIsFormModalOpen(false);
      setEditingBid(null);
      return data;
    } catch (err) {
      throw (err instanceof Error ? err : new Error(err?.message || 'Failed to create bid'));
    }
  };

  const handleUpdateBid = async (id, bidData) => {
    try {
      const { data, error: updateError } = await bidService?.update(id, bidData);
      if (updateError) throw (updateError instanceof Error ? updateError : new Error(updateError?.message || 'Failed to update bid'));
      await loadBids();
      setIsFormModalOpen(false);
      setEditingBid(null);
      return data;
    } catch (err) {
      throw (err instanceof Error ? err : new Error(err?.message || 'Failed to update bid'));
    }
  };

  const handleDeleteBid = async (id) => {
    try {
      const { error: deleteError } = await bidService?.delete(id);
      if (deleteError) throw (deleteError instanceof Error ? deleteError : new Error(deleteError?.message || 'Failed to delete bid'));
      await loadBids();
      setIsDetailModalOpen(false);
      setSelectedBid(null);
    } catch (err) {
      throw (err instanceof Error ? err : new Error(err?.message || 'Failed to delete bid'));
    }
  };

  const handleEditBid = (bid) => { setEditingBid(bid); setIsFormModalOpen(true); };
  const handleDuplicateBid = (bid) => { setEditingBid({ ...bid, bidNumber: null, status: 'Draft' }); setIsFormModalOpen(true); };
  const handleViewBid = (bid) => { setSelectedBid(bid); setIsDetailModalOpen(true); };
  const handleConvertToProject = (bid) => { navigate('/project-management', { state: { convertFromBid: bid } }); };
  const handleFormSubmit = async (formData) => {
    if (editingBid) return await handleUpdateBid(editingBid?.id, formData);
    else return await handleCreateBid(formData);
  };

  const handleBidTypeSelect = (typeId) => {
    setIsTypeSelectionOpen(false);
    if (typeId === 'summary') {
      setEditingBid(null);
      setIsFormModalOpen(true);
    } else if (typeId === 'simple-proposal') {
      navigate('/bid-management/simple-proposal');
    } else if (typeId === 'phased-proposal') {
      navigate('/bid-management/phased-proposal');
    } else if (typeId === 'detailed-proposal') {
      navigate('/bid-management/detailed-proposal');
    } else if (typeId === 'letter') {
      navigate('/bid-management/letter');
    }
  };

  const filteredAndSortedBids = useMemo(() => {
    let result = [...bids];
    if (filters?.search) {
      const q = filters?.search?.toLowerCase();
      result = result?.filter(bid =>
        bid?.bidNumber?.toLowerCase()?.includes(q) ||
        bid?.customerName?.toLowerCase()?.includes(q) ||
        bid?.projectName?.toLowerCase()?.includes(q)
      );
    }
    if (filters?.status !== 'all') {
      result = result?.filter(bid => bid?.status === filters?.status);
    }
    result?.sort((a, b) => {
      let aVal, bVal;
      switch (sort?.column) {
        case 'bidNumber': aVal = a?.bidNumber || ''; bVal = b?.bidNumber || ''; break;
        case 'customerName': aVal = a?.customerName || ''; bVal = b?.customerName || ''; break;
        case 'projectName': aVal = a?.projectName || ''; bVal = b?.projectName || ''; break;
        case 'totalAmount': aVal = parseFloat(a?.totalAmount) || 0; bVal = parseFloat(b?.totalAmount) || 0; break;
        case 'createdDate': aVal = new Date(a?.createdDate); bVal = new Date(b?.createdDate); break;
        case 'projectStartDate': aVal = a?.projectStartDate || ''; bVal = b?.projectStartDate || ''; break;
        case 'projectEndDate': aVal = a?.projectEndDate || ''; bVal = b?.projectEndDate || ''; break;
        case 'status': aVal = a?.status || ''; bVal = b?.status || ''; break;
        default: aVal = new Date(a?.createdDate); bVal = new Date(b?.createdDate);
      }
      if (typeof aVal === 'string') {
        return sort?.direction === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      }
      return sort?.direction === 'asc' ? (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) : (bVal < aVal ? -1 : bVal > aVal ? 1 : 0);
    });
    return result;
  }, [bids, filters, sort]);

  const activeColumns = ALL_COLUMNS?.filter(c => visibleColumns?.includes(c?.key));
  const hasActiveFilters = filters?.search || filters?.status !== 'all';

  const totalPages = Math.ceil((filteredAndSortedBids?.length || 0) / pageSize);
  const paginatedBids = filteredAndSortedBids?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const SortIcon = ({ colKey }) => {
    if (sort?.column !== colKey) return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground/50 ml-1" />;
    return sort?.direction === 'asc'
      ? <Icon name="ChevronUp" size={14} className="text-accent ml-1" />
      : <Icon name="ChevronDown" size={14} className="text-accent ml-1" />;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })?.format(amount);
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = String(dateString)?.split('T')?.[0]?.split('-');
    if (parts?.length !== 3) return dateString;
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">Bid Management</h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Create, track, and manage construction project bids with automated numbering and financial validation
            </p>
          </div>
          <Button variant="default" size="lg" onClick={() => setIsTypeSelectionOpen(true)} iconName="Plus" iconPosition="left" iconSize={20}>
            Create New Bid
          </Button>
        </div>

        {/* Summary Stats */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <div className="bg-card rounded-lg border border-border p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={14} className="text-accent" />
                </div>
                <span className="text-xs font-medium text-muted-foreground truncate">Total</span>
              </div>
              <p className="text-2xl font-bold text-foreground leading-none mb-1">{bids?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Total Bids</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-md bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Clock" size={14} className="text-yellow-500" />
                </div>
                <span className="text-xs font-medium text-muted-foreground truncate">Status</span>
              </div>
              <p className="text-2xl font-bold text-foreground leading-none mb-1">
                {bids?.filter(b => b?.status === 'Draft' || b?.status === 'Submitted')?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">Draft / Submitted</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-md bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="CheckCircle2" size={14} className="text-green-500" />
                </div>
                <span className="text-xs font-medium text-muted-foreground truncate">Status</span>
              </div>
              <p className="text-2xl font-bold text-foreground leading-none mb-1">
                {bids?.filter(b => b?.status === 'Accepted')?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">Accepted</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="DollarSign" size={14} className="text-accent" />
                </div>
                <span className="text-xs font-medium text-muted-foreground truncate">Value</span>
              </div>
              <p className="text-xl font-bold text-foreground leading-none mb-1">
                ${((bids?.reduce((sum, b) => sum + (parseFloat(b?.totalAmount) || 0), 0)) / 1000000)?.toFixed(2)}M
              </p>
              <p className="text-xs text-muted-foreground">Total Bid Value</p>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search bids..."
                value={filters?.search}
                onChange={e => handleFilterChange('search', e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
            </div>
            {/* Status Filter */}
            <select
              value={filters?.status}
              onChange={e => handleFilterChange('status', e?.target?.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            >
              <option value="all">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Submitted">Submitted</option>
              <option value="Under Review">Under Review</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Converted">Converted</option>
            </select>
            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors whitespace-nowrap"
              >
                <Icon name="X" size={14} />
                Clear
              </button>
            )}
            {/* Column Picker */}
            <div className="relative" ref={columnPickerRef}>
              <button
                onClick={() => setShowColumnPicker(v => !v)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg bg-background hover:bg-muted/50 text-foreground transition-colors whitespace-nowrap"
              >
                <Icon name="Columns" size={14} />
                Columns
                <span className="ml-1 text-xs bg-accent/10 text-accent rounded px-1">
                  {visibleColumns?.filter(k => k !== 'actions')?.length}
                </span>
              </button>
              {showColumnPicker && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-lg p-3 min-w-[180px]">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">Show Columns</p>
                  {ALL_COLUMNS?.filter(c => c?.key !== 'actions')?.map(col => (
                    <label key={col?.key} className={`flex items-center gap-2 px-1 py-1.5 rounded cursor-pointer hover:bg-muted/50 ${col?.required ? 'opacity-60 cursor-not-allowed' : ''}`}>
                      <input
                        type="checkbox"
                        checked={visibleColumns?.includes(col?.key)}
                        onChange={() => handleToggleColumn(col?.key)}
                        disabled={col?.required}
                        className="rounded border-border text-accent focus:ring-accent/30"
                      />
                      <span className="text-sm text-foreground">{col?.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  {activeColumns?.map(col => (
                    <th key={col?.key} className="px-4 py-3 md:px-6 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {col?.sortable ? (
                        <button
                          onClick={() => handleSortChange(col?.key)}
                          className="flex items-center hover:text-foreground transition-colors"
                        >
                          {col?.label}
                          <SortIcon colKey={col?.key} />
                        </button>
                      ) : (
                        col?.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeColumns?.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="FileText" size={48} className="text-muted-foreground" />
                        <p className="text-base text-muted-foreground">Loading bids...</p>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={activeColumns?.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="AlertCircle" size={48} className="text-muted-foreground" />
                        <p className="text-base text-muted-foreground">Error loading bids: {error}</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedBids?.length > 0 ? (
                  paginatedBids?.map(bid => (
                    <tr key={bid?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                      {activeColumns?.map(col => (
                        <td key={col?.key} className="px-4 py-2 md:px-6">
                          {col?.key === 'bidNumber' && (
                            <button onClick={() => handleViewBid(bid)} className="font-medium text-accent hover:text-accent/80 transition-colors">
                              {bid?.bidNumber}
                            </button>
                          )}
                          {col?.key === 'customerName' && <span className="text-sm text-foreground">{bid?.customerName}</span>}
                          {col?.key === 'projectName' && <span className="text-sm text-foreground">{bid?.projectName}</span>}
                          {col?.key === 'totalAmount' && <span className="text-sm font-semibold text-foreground">{formatCurrency(bid?.totalAmount)}</span>}
                          {col?.key === 'createdDate' && <span className="text-sm text-muted-foreground">{formatDate(bid?.createdDate)}</span>}
                          {col?.key === 'projectStartDate' && <span className="text-sm text-muted-foreground">{formatDate(bid?.projectStartDate)}</span>}
                          {col?.key === 'projectEndDate' && <span className="text-sm text-muted-foreground">{formatDate(bid?.projectEndDate)}</span>}
                          {col?.key === 'status' && <BidStatusBadge status={bid?.status} />}
                          {col?.key === 'actions' && (
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => handleEditBid(bid)} title="Edit bid">
                                <Icon name="Pencil" size={16} />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDuplicateBid(bid)} title="Duplicate bid">
                                <Icon name="Copy" size={16} />
                              </Button>
                              {bid?.status === 'Accepted' && (
                                <Button variant="ghost" size="icon" onClick={() => handleConvertToProject(bid)} title="Convert to project">
                                  <Icon name="ArrowRight" size={16} />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={activeColumns?.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="FileText" size={48} className="text-muted-foreground" />
                        <p className="text-base text-muted-foreground">No bids found</p>
                        <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear Filters</Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls - Desktop */}
          {!loading && !error && filteredAndSortedBids?.length > 0 && (
            <div className="hidden lg:flex items-center justify-between px-6 py-3 border-t border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e?.target?.value)); setCurrentPage(1); }}
                  className="px-2 py-1 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent min-w-[80px]"
                >
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={60}>60</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredAndSortedBids?.length)} of {filteredAndSortedBids?.length}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-border bg-background hover:bg-muted/50 text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="text-sm font-medium text-foreground">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-border bg-background hover:bg-muted/50 text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Mobile Cards */}
          <div className="lg:hidden p-4">
            {loading ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <Icon name="FileText" size={48} className="text-muted-foreground" />
                <p className="text-base text-muted-foreground">Loading bids...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <Icon name="FileText" size={48} className="text-muted-foreground" />
                <p className="text-base text-muted-foreground">Error loading bids: {error}</p>
              </div>
            ) : paginatedBids?.length > 0 ? (
              <>
                {paginatedBids?.map(bid => (
                  <BidMobileCard
                    key={bid?.id}
                    bid={bid}
                    onEdit={handleEditBid}
                    onDuplicate={handleDuplicateBid}
                    onConvert={handleConvertToProject}
                    onView={handleViewBid}
                  />
                ))}
                {/* Mobile Pagination */}
                <div className="flex items-center justify-between pt-3 border-t border-border mt-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e?.target?.value)); setCurrentPage(1); }}
                      className="px-2 py-1 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none min-w-[80px]"
                    >
                      <option value={20}>20</option>
                      <option value={40}>40</option>
                      <option value={60}>60</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredAndSortedBids?.length)} of {filteredAndSortedBids?.length}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1 rounded border border-border bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Icon name="ChevronLeft" size={14} />
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1 rounded border border-border bg-background text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Icon name="ChevronRight" size={14} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-3 py-12">
                <Icon name="FileText" size={48} className="text-muted-foreground" />
                <p className="text-base text-muted-foreground">No bids found</p>
                <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear Filters</Button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 md:p-6">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-accent mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p className="text-sm text-foreground">
                <strong>Bid Numbering:</strong> Bids are automatically numbered using the format B + 2-digit year + 5-digit sequence (e.g., B2600001 for the first bid of 2026).
              </p>
              <p className="text-sm text-foreground">
                <strong>Financial Validation:</strong> The total project amount must exactly equal the sum of Labor Amount + Equipment Rental + Supplies &amp; Materials.
              </p>
              <p className="text-sm text-foreground">
                <strong>Converting to Projects:</strong> Accepted bids can be converted to projects, which will automatically create a project with the bid information and generate a project number.
              </p>
            </div>
          </div>
        </div>
      </div>
      <BidFormModal
        isOpen={isFormModalOpen}
        onClose={() => { setIsFormModalOpen(false); setEditingBid(null); }}
        onSubmit={handleFormSubmit}
        editBid={editingBid}
      />
      <BidDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); setSelectedBid(null); }}
        bid={selectedBid}
        onConvert={handleConvertToProject}
        onEdit={handleEditBid}
        onDelete={handleDeleteBid}
        onArchived={() => { setIsDetailModalOpen(false); setSelectedBid(null); loadBids(); }}
      />
      <BidTypeSelectionModal
        isOpen={isTypeSelectionOpen}
        onClose={() => setIsTypeSelectionOpen(false)}
        onSelectType={handleBidTypeSelect}
      />
    </MainLayout>
  );
};

export default BidManagement;